import React, { useCallback, useEffect } from 'react';
import { toJSON } from '@atlaskit/editor-common/utils';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports

import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import { createAIPromptPreset } from '../presets/prompt';

// PromptEditorProps is mirrored at `packages/editor/generative-ai-modal/src/ui/components/PromptEditorWrapper/PromptEditorWrapper.tsx`
// since editor-ai-injected-editors cannot be a dependency of generative-ai-modal or editor-plugin-ai.

const EditorWrapper = props => {
  const appearance = 'chromeless';
  const {
    editorProps,
    options
  } = props;
  const {
    onInputChange,
    onADFChange,
    autoFocus,
    defaultValue,
    placeholder,
    enableLinks,
    setFocusHandlers,
    setClearHandler
  } = editorProps;
  const onChange = useCallback(editorView => {
    if (onADFChange) {
      onADFChange(JSON.stringify(toJSON(editorView.state.doc)));
    }
    if (onInputChange) {
      const textSerializer = TextSerializer.fromSchema(editorView.state.schema);
      const text = textSerializer.serializeFragment(editorView.state.doc.content);
      onInputChange(text);
    }
  }, [onInputChange, onADFChange]);
  const onSave = useCallback(editorView => {
    onChange(editorView);
  }, [onChange]);

  // We could have created preset outside PromptEditor component,
  //  but currently placeholder prop ComposableEditor is not working.
  // We have to pass placeholder to placeholder plugin.
  // That's why using usePreset, who memoize preset.
  const {
    preset,
    editorApi
  } = usePreset(() => createAIPromptPreset({
    ...options,
    placeholder: {
      placeholder: editorProps.placeholder
    },
    onSave,
    enableLinks
  }), [options, editorProps.placeholder]);
  const onEditorReady = useCallback(editorActions => {
    if (setClearHandler) {
      setClearHandler(() => editorActions.clear());
    }
  }, [setClearHandler]);
  useEffect(() => {
    if (setFocusHandlers) {
      var _editorApi$core, _editorApi$core2;
      /**
       * We need to expose clear function through ref because
       *  changing defaultValue does not clear editor.
       */
      setFocusHandlers(editorApi === null || editorApi === void 0 ? void 0 : (_editorApi$core = editorApi.core) === null || _editorApi$core === void 0 ? void 0 : _editorApi$core.actions.blur, editorApi === null || editorApi === void 0 ? void 0 : (_editorApi$core2 = editorApi.core) === null || _editorApi$core2 === void 0 ? void 0 : _editorApi$core2.actions.focus);
    }
  }, [editorApi, setFocusHandlers]);
  return /*#__PURE__*/React.createElement(ComposableEditor, {
    appearance: appearance,
    minHeight: 20,
    preset: preset,
    shouldFocus: autoFocus,
    defaultValue: defaultValue,
    placeholder: placeholder,
    onChange: onChange
    // we need to set linking here in props as setting it in preset options doesn't work.
    ,
    linking: options.linking
    // We will remove use of onEditorReady when editorApi provides a matching "clear()" action.
    ,
    onEditorReady: onEditorReady
  });
};
export function createPromptEditor(options) {
  return props => {
    const editorProps = {
      editorProps: props,
      options
    };
    return /*#__PURE__*/React.createElement(EditorContext, null, /*#__PURE__*/React.createElement(EditorWrapper, editorProps));
  };
}